// ============================================================================
//  ClaimInvestigations — the Investigations table inside the inline claim editor
// ----------------------------------------------------------------------------
//  Same rhythm as Procedures and Diagnoses: Add a row, fill it in, save it, Add
//  the next. What differs is what you pick BY:
//
//    INVESTIGATION  the searchable field. Keyed on the NAME, because 65 of the 115
//                   lab_prices rows have no G-DRG at all — a picker keyed on the
//                   code could not offer them, and they are real tests.
//    G-DRG          derived from the catalogue. Read-only, no dropdown. Blank where
//                   the catalogue has none, said plainly rather than left ambiguous.
//    KIND           Lab or Scan, from which catalogue the entry came. This is what
//                   claim_investigations.kind holds and what the flat model has
//                   always recorded as investigation.lab_scan.
//    DATE           the visit's own days, exactly as a procedure's date is bounded.
//
//  The RESULT is deliberately not editable here. It is the laboratory's answer,
//  not the claims officer's, and a claims screen inventing one would be worse than
//  leaving it empty.
// ============================================================================
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { cachedGet } from './catalogueCache';
//  Pencil on Edit - see the note in ClaimDiagnoses.js.
import { Save, Trash2, X, Loader2, Pencil } from 'lucide-react';
import {
  claimStoreInvestigationTariff, claimStoreInvestigation, claimStoreProcedureDates,
} from '../BaseURL';
import SearchSelect from './SearchSelect';
import VisitDateSelect from './VisitDateSelect';
import { confirmRemove } from './claimsDialog';

const BRAND = {
  green: '#1A9B7F', greenDark: '#137a63', greenSoft: '#e8f5f1',
  line: '#e2e8f0', ink: '#0f172a', inkSoft: '#475569', inkFaint: '#94a3b8',
  head: '#f1f5f9', danger: '#b91c1c', dangerSoft: '#fef2f2',
  amber: '#b45309', amberSoft: '#fffbeb', navy: '#1e3a5f', navySoft: '#eef2f8',
};

const th = {
  textAlign: 'left', padding: '9px 10px', fontSize: 10.5, fontWeight: 800,
  letterSpacing: 0.4, textTransform: 'uppercase', color: BRAND.inkSoft,
  background: BRAND.head, borderBottom: `1px solid ${BRAND.line}`, whiteSpace: 'nowrap',
};
const td = {
  padding: '8px 10px', fontSize: 12, color: BRAND.ink,
  borderBottom: `1px solid ${BRAND.line}`, verticalAlign: 'middle',
};
const cell = {
  width: '100%', boxSizing: 'border-box', padding: '6px 8px', fontSize: 12,
  border: `1px solid ${BRAND.green}`, borderRadius: 7, background: '#fff',
  color: BRAND.ink, outline: 'none', fontFamily: 'inherit',
};
const iconBtn = (tone) => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 28, height: 28, borderRadius: 7, cursor: 'pointer',
  border: `1px solid ${tone === 'danger' ? '#fecaca' : BRAND.line}`,
  background: tone === 'danger' ? BRAND.dangerSoft : '#fff',
  color: tone === 'danger' ? BRAND.danger : BRAND.greenDark,
});

//  `dmy` was here in triplicate across this file and its two sibling tables. Every
//  call site was a date cell, and those are VisitDateSelect now, which formats its
//  own value.

//  claim_investigations.kind is ENUM('lab','imaging','other') — lowercase, and it
//  does NOT include 'scan'. This MySQL is not in STRICT mode, so writing 'Scan'
//  stored an empty string with no error at all. The stored values are used
//  everywhere; only the label shown is capitalised.
const KIND_LABEL = { lab: 'Lab', imaging: 'Imaging', other: 'Other' };

const kindChip = (kind) => {
  const k = String(kind || 'lab').toLowerCase();
  const imaging = k === 'imaging';
  return (
    <span style={{
      padding: '1px 6px', borderRadius: 4, fontSize: 9.5, fontWeight: 800,
      background: imaging ? BRAND.navySoft : BRAND.greenSoft,
      color: imaging ? BRAND.navy : BRAND.greenDark,
    }}>{KIND_LABEL[k] || k}</span>
  );
};

const blankRow = (firstDate) => ({
  id: null, code: '', description: '', kind: 'lab', tariff: null,
  performed_at: firstDate || '',
});

export default function ClaimInvestigations({
  claimId, source, scheme, rows = [], onSaved, readOnly,
  addSignal = false, onAddConsumed,
}) {
  const [catalogue, setCatalogue] = useState([]);
  const [catNote, setCatNote] = useState(null);
  const [dates, setDates] = useState([]);
  const [dateNote, setDateNote] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  //  Priced per scheme, like every other catalogue on this screen.
  useEffect(() => {
    let alive = true;
    cachedGet(claimStoreInvestigationTariff, { scheme })
      .then((data) => {
        if (!alive) return;
        setCatalogue(data?.options || []);
        setCatNote(data?.note || null);
      })
      .catch(() => {
        if (!alive) return;
        setCatalogue([]);
        setCatNote('The investigation list could not be loaded.');
      });
    return () => { alive = false; };
  }, [scheme]);

  //  The same window a procedure's date is bounded by — one endpoint serves both,
  //  because the question is about the claim's visit and not about the table.
  const loadDates = useCallback(() => {
    if (!claimId) return;
    //  ttl: 0 — deduped against the other two tables asking in the same commit,
    //  never served from store. A save can extend the stay, and this is re-run
    //  after one; a stored answer would be the list from before it.
    cachedGet(claimStoreProcedureDates, { id: claimId, source }, { ttl: 0 })
      .then((data) => { setDates(data?.dates || []); setDateNote(data?.note || null); })
      .catch(() => { setDates([]); setDateNote(null); });
  }, [claimId, source]);
  useEffect(loadDates, [loadDates]);

  const addRow = useCallback(() => {
    setError(null);
    setDrafts((d) => [...d, {
      key: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      ...blankRow(dates[0]),
    }]);
  }, [dates]);

  useEffect(() => {
    if (!addSignal) return;
    if (!readOnly) addRow();
    if (onAddConsumed) onAddConsumed();
  }, [addSignal, addRow, readOnly, onAddConsumed]);

  const patch = (key, k, v) => setDrafts((d) =>
    d.map((x) => (x.key === key ? { ...x, [k]: v } : x)));

  //  Picking the test brings its code, its kind and its tariff. The OPTION is used
  //  rather than a lookup, because identity is kind + name and a name alone does
  //  not say which catalogue the entry came from.
  //  Already on the claim? Matched on the NAME and the day — the name because most
  //  catalogue rows have no code, and the day because a repeat test on another day
  //  of a stay is a real second test. Told at the moment of picking; the server
  //  refuses it too.
  //  A ROW IS NOT A DUPLICATE OF ITSELF. See the note in ClaimDiagnoses.js -
  //  substituting one test for another on a saved row was refused with "already
  //  on this claim", naming the row being replaced.
  const duplicateOf = (key, name, when, selfId) => {
    const same = (a, b) => String(a || '') === String(b || '');
    const isSelf = (r) => selfId != null && String(r.id) === String(selfId);
    if (rows.some((r) => !isSelf(r) && same(r.description, name)
      && same(r.performed_at, when))) {
      return 'saved';
    }
    return drafts.some((x) => x.key !== key && same(x.description, name)
      && same(x.performed_at, when)) ? 'draft' : null;
  };

  const pickInvestigation = (key, _id, opt) => {
    if (!opt) return;
    const row = drafts.find((x) => x.key === key);
    const dup = duplicateOf(key, opt.name, row?.performed_at || dates[0], row?.id);
    setError(dup
      ? `${opt.name} is already on this claim`
        + `${dup === 'draft' ? ' — in a row above, not yet saved' : ''}.`
      : null);
    setDrafts((d) => d.map((x) => (x.key === key ? {
      ...x,
      description: opt.name || '',
      code: opt.code || '',
      kind: opt.kind || 'lab',
      tariff: opt.tariff ?? null,
      duplicate: Boolean(dup),
    } : x)));
  };

  //  The stored enum value, not the label — the identity has to match on both
  //  sides and 'Lab' is not what the column holds.
  const idOf = (o) => `${o.kind || 'lab'}::${o.name}`;
  const valueOf = (row) => `${row.kind || 'lab'}::${row.description || ''}`;

  const drop = (key) => setDrafts((d) => d.filter((x) => x.key !== key));

  const editRow = (r) => {
    setError(null);
    setDrafts((d) => (d.some((x) => x.id === r.id) ? d : [...d, { key: `row-${r.id}`, ...r }]));
  };

  const save = async (row) => {
    if (!row.description) { setError('Pick an investigation first.'); return; }
    //  Re-checked: the date can change after the test was chosen.
    const dup = duplicateOf(row.key, row.description, row.performed_at || dates[0], row.id);
    if (dup) {
      setError(`${row.description} is already on this claim`
        + `${dup === 'draft' ? ' — in a row above, not yet saved' : ''}.`);
      return;
    }
    setBusy(row.key); setError(null);
    try {
      const { data } = await axios[row.id ? 'put' : 'post'](claimStoreInvestigation, {
        id: row.id || undefined,
        description: row.description,
        code: row.code || null,
        kind: row.kind || 'lab',
        tariff: row.tariff,
        performed_at: row.performed_at || dates[0] || null,
      }, { params: { id: claimId, source } });
      drop(row.key);
      if (data && onSaved) onSaved(data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not save the investigation.');
    } finally { setBusy(null); }
  };

  const remove = async (r) => {
    // eslint-disable-next-line no-alert
    if (!(await confirmRemove({ what: 'investigation', name: r.description || r.code }))) return;
    setBusy(`row-${r.id}`); setError(null);
    try {
      const { data } = await axios.delete(claimStoreInvestigation,
        { params: { id: claimId, source, rowId: r.id } });
      if (data && onSaved) onSaved(data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not remove the investigation.');
    } finally { setBusy(null); }
  };

  //  One draft row. A FUNCTION rather than inline JSX because it renders in
  //  two places: a new row at the top of the table, an edited row in the
  //  position of the row it replaces. Clicking Edit must not move anything.
  const draftTr = (row, i) => (
                <tr key={row.key} style={{
                  background: row.duplicate ? BRAND.dangerSoft : BRAND.greenSoft }}>
                  <td style={{ ...td, color: BRAND.inkFaint }}>{'\u2014'}</td>
                  {/*  Searched by NAME — that is what the catalogue reliably has. */}
                  <td style={td}>
                    <SearchSelect
                      options={catalogue}
                      value={valueOf(row)}
                      idOf={idOf}
                      onChange={(id, opt) => pickInvestigation(row.key, id, opt)}
                      placeholder="Select investigation"
                      searchPlaceholder="Type a test name or code…"
                      //  A row ADDED opens its list with the cursor already in the
                      //  search box. Not a saved row opened for editing — see the
                      //  note in ClaimDiagnoses.
                      autoOpen={!row.id}
                      ariaLabel="Investigation"
                      //  The closed field shows the NAME here, because that is what
                      //  this column is headed and what identifies the test.
                      triggerOf={(o) => <span>{o.name}</span>}
                      primaryOf={(o) => (
                        <span>
                          {o.name}
                          <span style={{ marginLeft: 6 }}>{kindChip(o.kind)}</span>
                        </span>
                      )}
                      secondaryOf={(o) => [
                        o.code ? `G-DRG ${o.code}` : 'no G-DRG in the catalogue',
                        o.tariff ? `GHS ${Number(o.tariff).toFixed(2)}` : null,
                        o.topup ? `top-up GHS ${Number(o.topup).toFixed(2)}` : null,
                      ].filter(Boolean).join('  ·  ')} />
                  </td>
                  {/*  Derived. Read-only, and blank where the catalogue has none —
                       which is most of it, so silence would read as a failure. */}
                  <td style={{ ...td, fontWeight: 700 }}>
                    {row.code || (
                      <span style={{ fontWeight: 400, color: BRAND.inkFaint }}>
                        {row.description ? 'none in the catalogue' : '—'}
                      </span>
                    )}
                  </td>
                  <td style={td}>{row.description ? kindChip(row.kind) : '—'}</td>
                  <td style={td}>{dateCell(row)}</td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: 5 }}>
                      <button type="button" title="Save" style={iconBtn()}
                        aria-label="Save investigation"
                        disabled={busy === row.key}
                        onClick={() => save(row)}>
                        {busy === row.key
                          ? <Loader2 size={13} className="spin" />
                          : <Save size={13} />}
                      </button>
                      <button type="button" title="Discard" style={iconBtn('danger')}
                        aria-label="Discard row"
                        onClick={() => drop(row.key)}><X size={13} /></button>
                    </div>
                  </td>
                </tr>
  );

  //  New rows only — an edited row is drawn where it already sits.
  const newDrafts = drafts.filter((d) => !d.id);
  const draftFor = (id) => drafts.find((d) => d.id != null && d.id === id);

  //  Date order, ties broken by id. See the note in ClaimProcedures: drafts are
  //  typed at the top where they can be seen, and settle into their own day on save.
  const saved = rows
    .slice()
    .sort((a, b) => String(a.performed_at || '').localeCompare(String(b.performed_at || ''))
      || (Number(a.id) || 0) - (Number(b.id) || 0));
  const isEmpty = saved.length === 0 && drafts.length === 0;

  const dateCell = (row) => (
    <VisitDateSelect
      value={row.performed_at || dates[0]}
      dates={dates}
      style={cell}
      onChange={(d) => patch(row.key, 'performed_at', d)}
    />
  );

  // ── Moving a SAVED test to another day of the stay ────────────────────────
  //  The same control the draft row has. The PUT carries every field because this
  //  endpoint replaces the row — sending the date alone would blank the result.
  const changeSavedDate = async (r, when) => {
    if (!when || when === String(r.performed_at || '').slice(0, 10)) return;
    setBusy(`date-${r.id}`); setError(null);
    try {
      const { data } = await axios.put(claimStoreInvestigation, {
        id: r.id,
        description: r.description,
        code: r.code || null,
        kind: r.kind || 'lab',
        tariff: r.tariff,
        performed_at: when,
      }, { params: { id: claimId, source } });
      if (data && onSaved) onSaved(data);
    } catch (e) {
      setError(e?.response?.data?.message
        || 'Could not move that investigation to another day.');
    } finally { setBusy(null); }
  };

  return (
    <div>
      {(dateNote || catNote) && (
        <div style={{ display: 'grid', gap: 5, marginBottom: 8 }}>
          {dateNote && (
            <div style={{ fontSize: 10.5, color: BRAND.inkFaint }}>{dateNote}</div>
          )}
          {catNote && (
            <div style={{
              fontSize: 10.5, padding: '6px 9px', borderRadius: 6,
              background: BRAND.amberSoft, color: BRAND.amber,
              border: '1px solid #fde68a',
            }}>{catNote}</div>
          )}
        </div>
      )}

      {error && (
        <div style={{
          marginBottom: 8, padding: '7px 10px', borderRadius: 7, fontSize: 11.5,
          background: BRAND.dangerSoft, color: BRAND.danger,
          border: '1px solid #fecaca',
        }}>{error}</div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 660, borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...th, width: 34 }}>#</th>
              <th style={th}>Investigation</th>
              <th style={{ ...th, width: 100 }}>G-DRG</th>
              <th style={{ ...th, width: 64 }}>Kind</th>
              <th style={{ ...th, width: 130 }}>Date</th>
              <th style={{ ...th, width: 76 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {/*  A duplicate draft row turns red, so the message above names which
                 row it is talking about. */}
            {newDrafts.map(draftTr)}

            {isEmpty && (
              <tr><td colSpan={6} style={{
                ...td, textAlign: 'center', color: BRAND.inkFaint, padding: '22px 10px',
              }}>No investigations available</td></tr>
            )}

            {saved.map((r, i) => {
              //  Being edited? Draw the draft HERE, in this row's place.
              const d = draftFor(r.id);
              if (d) return draftTr(d, i);
              return (
              <tr key={r.id}>
                <td style={{ ...td, color: BRAND.inkFaint }}>{i + 1}</td>
                <td style={td}>
                  {r.description}
                  {/*  The laboratory's answer where there is one. Shown, never
                       edited from here. */}
                  {r.result && (
                    <div style={{ fontSize: 10.5, color: BRAND.inkSoft, marginTop: 2 }}>
                      {r.result}
                    </div>
                  )}
                </td>
                <td style={{ ...td, fontWeight: 700 }}>{r.code || '—'}</td>
                <td style={td}>{kindChip(r.kind)}</td>
                <td style={td}>
                  <VisitDateSelect
                    value={r.performed_at}
                    dates={dates}
                    disabled={readOnly}
                    busy={busy === `date-${r.id}`}
                    ariaLabel={`Day for ${r.description || r.code}`}
                    onChange={(d) => changeSavedDate(r, d)}
                  />
                </td>
                <td style={td}>
                  {!readOnly && (
                    <div style={{ display: 'flex', gap: 5 }}>
                      <button type="button"
                        title="Change this test - pick another and save"
                        style={iconBtn()}
                        aria-label={`Edit ${r.description || r.code}`}
                        onClick={() => editRow(r)}><Pencil size={13} /></button>
                      <button type="button" title="Remove" style={iconBtn('danger')}
                        aria-label={`Remove ${r.description || r.code}`}
                        onClick={() => remove(r)}><Trash2 size={13} /></button>
                    </div>
                  )}
                </td>
              </tr>
              );
            })}

          </tbody>
        </table>
      </div>
    </div>
  );
}
